import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import "./productQuoteForm.css";

const ProductQuoteForm = ({ product, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    quantity: "",
    location: "",
    message: "",
    product: product?.name || "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (product?.name) {
      setFormData((prev) => ({
        ...prev,
        product: product.name,
      }));
    }
  }, [product]);

  const validate = () => {
    let newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    } else if (!/^[A-Za-z\s]{3,}$/.test(formData.name.trim())) {
      newErrors.name = "Enter a valid name.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Enter a valid email.";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required.";
    } else if (!/^[6-9]\d{9}$/.test(formData.phone.trim())) {
      newErrors.phone = "Enter a valid 10-digit number.";
    }

    if (!formData.location.trim()) {
      newErrors.location = "Location is required.";
    }

    if (!formData.quantity.trim()) {
      newErrors.quantity = "Quantity is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (loading) return;
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/quotes`, formData, { timeout: 8000 });
      if (response.status === 200 || response.status === 201) {
        alert("✅ Quote Request Submitted Successfully!");
        onClose();
      }
    } catch (err) {
      console.error("Quote Submit Error:", err);
      alert("⚠ Error submitting quote, please try again.");
    }
    setLoading(false);
  };

  if (!product) return null;

  return (
    <div className="pq-modal-overlay">
      <div className="pq-modal-container">
        <button className="pq-close-btn" onClick={onClose}>&times;</button>
        
        <div className="pq-layout">
          {/* 1. PRODUCT CONTEXT SIDE */}
          <div className="pq-context">
            <div className="pq-badge">Industrial Fleet Inquiry</div>
            {product?.image && (
              <div className="pq-image-wrapper">
                <img src={`${API_BASE_URL}${product.image}`} alt={product.name} />
              </div>
            )}
            <div className="pq-product-meta">
              <h3>{product.name}</h3>
              <p className="pq-description">{product.description || 'Professional autonomous UAV system designed for enterprise-grade operations.'}</p>
              <div className="pq-trust-points">
                <span>✓ Direct Manufacturing Support</span>
                <span>✓ DGCA Certified Systems</span>
                <span>✓ 24/7 Technical Assistance</span>
              </div>
            </div>
          </div>

          {/* 2. FORM ENTRY SIDE */}
          <div className="pq-form-side">
            <h2 className="pq-form-title">Request Enterprise Quote</h2>
            <p className="pq-form-subtitle">Fill in the technical requirements and our engineers will contact you within 24 hours.</p>

            <form onSubmit={handleSubmit} className="pq-main-form">
              <div className="pq-form-grid">
                <div className="pq-input-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="John Doe"
                    className={errors.name ? "pq-error" : ""}
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {errors.name && <span className="pq-err-msg">{errors.name}</span>}
                </div>

                <div className="pq-input-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="john@company.com"
                    className={errors.email ? "pq-error" : ""}
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && <span className="pq-err-msg">{errors.email}</span>}
                </div>

                <div className="pq-input-group">
                  <label>Contact Number</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+91 XXXXX XXXXX"
                    className={errors.phone ? "pq-error" : ""}
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  {errors.phone && <span className="pq-err-msg">{errors.phone}</span>}
                </div>

                <div className="pq-input-group">
                  <label>Enterprise / Company</label>
                  <input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>

                <div className="pq-input-group">
                  <label>Fleet Quantity</label>
                  <input
                    type="text"
                    name="quantity"
                    placeholder="Units required"
                    className={errors.quantity ? "pq-error" : ""}
                    value={formData.quantity}
                    onChange={handleChange}
                  />
                  {errors.quantity && <span className="pq-err-msg">{errors.quantity}</span>}
                </div>

                <div className="pq-input-group">
                  <label>Deployment Location</label>
                  <input
                    type="text"
                    name="location"
                    placeholder="City, State"
                    className={errors.location ? "pq-error" : ""}
                    value={formData.location}
                    onChange={handleChange}
                  />
                  {errors.location && <span className="pq-err-msg">{errors.location}</span>}
                </div>
              </div>

              <div className="pq-input-group full">
                <label>Technical Requirements / Message</label>
                <textarea
                  name="message"
                  placeholder="Special payloads, endurance requirements, or custom configurations..."
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
              </div>

              <button type="submit" className="pq-submit-btn" disabled={loading}>
                {loading ? "Processing..." : "Submit Technical Inquiry"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductQuoteForm;
